import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { cloneDeep } from 'lodash'

import {
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Fab,
  Tooltip,
} from '@mui/material'

import { Add } from '@mui/icons-material'
import { formatInBrazilianDate, hasAnswerChanged } from 'utils/functions'
import { addKeyToObjectsArray } from 'utils/addUniqueKeyToObjects'
import { OPERATION_TYPES } from 'utils/constants/operationTypes'

import styles from './styles'
import AnswerCard from '../AnswerCard'
import QuestionButton from '../QuestionButton'

export default function QuestionEighteen(props) {
  const { questionData, saveResponses } = props
  const classes = styles()

  const initialData = {
    developed_activities: '',
    start_date: '',
    end_date: '',
    exit_reason: '',
  }

  const initializeFormState = (data) => {
    return {
      hasRelationWithAnotherCompanies: data.items?.length > 0,
      items: data.items,
    }
  }

  const [formState, setFormState] = useState(initializeFormState(questionData))

  const [minimum, setMinimum] = useState('')

  const [activityData, setActivityData] = useState(initialData)

  const [activityErrors, setActivityErrors] = useState({
    developed_activities: false,
    start_date: false,
  })

  const [loading, setLoading] = useState(false)

  const [edited, setEdited] = useState(false)
  const [saved, setSaved] = useState(questionData.has_answer)

  const handleFieldChange = (event) => {
    const key = event.target.id
    const newActivityData = cloneDeep(activityData)
    newActivityData[key] = event.target.value
    setActivityData(newActivityData)
  }

  const handleRadioChange = (event) => {
    const newFormState = cloneDeep(formState)
    newFormState.hasRelationWithAnotherCompanies = event.target.value === 'yes'
    setFormState(newFormState)
  }

  const hasActivities = () => {
    return !(formState.items.length === 0)
  }

  const validateAndSave = async (skipValidation = false) => {
    if (
      formState.items.length > 0 &&
      formState.hasRelationWithAnotherCompanies === false
    ) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
      return
    }
    const newQuestionData = cloneDeep(questionData)
    if (!skipValidation && formState.hasRelationWithAnotherCompanies && !hasActivities()) {
      toast.error(
        'Ao selecionar "Sim" deve-se adicionar ao menos uma atividade',
      )
    } else {
      newQuestionData.has_answer = true
      newQuestionData.items = formState.items
      setLoading(false)
      await saveResponses(
        newQuestionData,
        'relation_with_another_companies',
        !saved ? OPERATION_TYPES.CREATE : OPERATION_TYPES.UPDATE,
      )
        .then(() => {
          setSaved(true)
          setEdited(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const validateRequiredFields = () => {
    const newActivityErrors = {
      developed_activities: false,
      start_date: false,
    }

    if (activityData.developed_activities === '') {
      newActivityErrors.developed_activities = true
    }
    if (activityData.start_date === '') {
      newActivityErrors.start_date = true
    }

    setActivityErrors(newActivityErrors)

    return !Object.keys(newActivityErrors).some((key) => newActivityErrors[key])
  }

  const handleValidateAndUpdateState = () => {
    if (validateRequiredFields()) {
      const newFormState = cloneDeep(formState)
      const updatedActivityData = cloneDeep(activityData)
      if (updatedActivityData.end_date === '') {
        updatedActivityData.end_date = null
        updatedActivityData.exit_reason = null
      }
      newFormState.items.push(updatedActivityData)
      setFormState(newFormState)
      setActivityData(initialData)
    } else {
      toast.warn(
        'Favor, verifique se todos os campos obrigatórios foram preenchidos',
      )
    }
  }

  const handleRemoveActivity = (position) => {
    const newFormState = cloneDeep(formState)
    newFormState.items = newFormState.items.filter(
      (activity, index) => index !== position,
    )
    setFormState(newFormState)
  }

  const buildActivity = (activityInfo) => {
    return (
      <React.Fragment>
        <p>
          <span>Atividade Desempenhada: </span>
          {activityInfo.developed_activities}
        </p>
        <p>
          <span>Período: </span>
          {`${formatInBrazilianDate(activityInfo.start_date)} - ${
            activityInfo.end_date === '' || activityInfo.end_date === null
              ? 'atualmente'
              : formatInBrazilianDate(activityInfo.end_date)
          }`}
        </p>
        {activityInfo.exit_reason ? (
          <p>
            <span>Motivo da saída: </span>
            {activityInfo.exit_reason}
          </p>
        ) : null}
      </React.Fragment>
    )
  }

  useEffect(() => {
    setMinimum(activityData.start_date)
  }, [activityData.start_date])

  useEffect(() => {
    const hasChanged =
      hasAnswerChanged(questionData.items, formState.items) ||
      formState.hasRelationWithAnotherCompanies !==
        questionData.items.length > 0
    setEdited(hasChanged)
    if (
      formState.items.length > 0 &&
      formState.hasRelationWithAnotherCompanies === false
    ) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
    }
  }, [formState])

  useEffect(() => {
    setSaved(questionData.has_answer)
    setFormState(initializeFormState(questionData))
  }, [questionData])
  
  useEffect(() => {
    if (hasAnswerChanged(questionData.items, formState.items))
      validateAndSave(true)
  }, [formState.items])

  return (
    <div className={classes.root}>
      <div className={classes.formContainer}>
        <h3>Questão 18</h3>
        <div className={classes.form}>
          <FormControl>
            <FormLabel>
              Teve ou tem relações com outras instituições, tais como,
              corretoras ou distribuidoras de títulos e valores mobiliários ou
              outras instituições?{' '}
            </FormLabel>
            <RadioGroup
              row
              value={formState.hasRelationWithAnotherCompanies ? 'yes' : 'no'}
              name="hasRelations"
              onChange={handleRadioChange}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Sim" />
              <FormControlLabel value="no" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>

          {formState.hasRelationWithAnotherCompanies ? (
            <div className={classes.formFields}>
              <TextField
                id="developed_activities"
                label="Atividade Desempenhada"
                onChange={handleFieldChange}
                required
                fullWidth
                value={activityData.developed_activities}
                InputLabelProps={{ shrink: true }}
                error={activityErrors.developed_activities}
                size="small"
              />
              <div className={classes.dates}>
                <TextField
                  id="start_date"
                  label="Data de início"
                  onChange={handleFieldChange}
                  required
                  value={activityData.start_date}
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={activityErrors.start_date}
                  size="small"
                />
                <TextField
                  id="end_date"
                  label="Data de término (opcional)"
                  onChange={handleFieldChange}
                  value={activityData.end_date || ''}
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    inputProps: {
                      min: minimum,
                    },
                  }}
                  disabled={minimum === ''}
                  size="small"
                />
              </div>
              <TextField
                id="exit_reason"
                label="Motivo da saida (opcional)"
                onChange={handleFieldChange}
                value={activityData.exit_reason}
                multiline
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
              <div className={classes.fabContainer}>
                <Tooltip title="Adicionar atividade">
                  <Fab
                    onClick={handleValidateAndUpdateState}
                    disabled={formState?.items?.length === 10}
                    size="small"
                    color="primary"
                  >
                    <Add />
                  </Fab>
                </Tooltip>
              </div>
            </div>
          ) : null}
        </div>
        {formState?.items?.length > 0 ? (
          <div className={classes.answersContainer}>
            {addKeyToObjectsArray(formState.items)?.map((activity, index) => (
              <AnswerCard
                key={activity.key}
                index={index}
                handleRemove={handleRemoveActivity}
                mustRemove={formState.hasRelationWithAnotherCompanies === false}
              >
                {buildActivity(activity)}
              </AnswerCard>
            ))}
          </div>
        ) : null}
      </div>
      <div className={classes.buttonsContainer}>
        <QuestionButton
          variant="contained"
          onClick={validateAndSave}
          loading={loading}
          saved={saved}
          edited={edited}
          disabled={
            (formState.hasRelationWithAnotherCompanies &&
              formState.items.length === 0) ||
            (saved && !edited)
          }
        />
      </div>
    </div>
  )
}
